import { jsonGuard } from '@logto/connector-kit';
import { z } from 'zod';
import { InteractionEvent, interactionIdentifierGuard, } from '../interactions.js';
import { userInfoGuard } from '../user.js';
import { VerificationType } from '../verification-records/index.js';
/** Values map to config key names in `logto_configs`. */
export var LogtoActionKey;
(function (LogtoActionKey) {
    LogtoActionKey["PostFirstFactorVerification"] = "action.postFirstFactorVerification";
    LogtoActionKey["PostSignIn"] = "action.postSignIn";
})(LogtoActionKey || (LogtoActionKey = {}));
export const actionExecutionErrorPolicies = Object.freeze(['block', 'allow']);
export const logtoActionGuard = z
    .object({
    script: z.string(),
    environmentVariables: z.record(z.string(), z.string()).optional(),
    contextSample: jsonGuard.optional(),
    enabled: z.boolean().optional(),
    onExecutionError: z.enum(actionExecutionErrorPolicies).optional(),
})
    .strict();
/**
 * Shared execution payload for production Actions and Management API dry runs.
 * Cloud remote execution and the local runner both accept this shape.
 */
export const actionExecutionRequestBodyGuard = z
    .object({
    script: z.string(),
    actionType: z.nativeEnum(LogtoActionKey),
    event: jsonGuard,
    environmentVariables: z.record(z.string(), z.string()).optional(),
})
    .strict();
export const actionUserPatchGuard = userInfoGuard
    .pick({
    username: true,
    primaryEmail: true,
    primaryPhone: true,
    name: true,
    avatar: true,
    customData: true,
    profile: true,
})
    .partial();
// Nested `user` uses passthrough so password fields survive structural parsing.
export const postFirstFactorVerificationResultGuard = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('createUser'),
        user: actionUserPatchGuard.passthrough(),
        passwordVerified: z.literal(true),
    }),
    z.object({
        action: z.literal('updateUser'),
        user: actionUserPatchGuard.passthrough(),
        passwordVerified: z.literal(true),
    }),
]);
// Nested `user` uses passthrough so password fields survive structural parsing.
export const postSignInResultGuard = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('updateUser'),
        user: actionUserPatchGuard.passthrough().optional(),
    }),
]);
export const actionUserPatchFields = Object.freeze(actionUserPatchGuard.keyof().options);
export const loggablePostFirstFactorVerificationEventGuard = z.object({
    key: z.literal(LogtoActionKey.PostFirstFactorVerification),
    interactionEvent: z.literal(InteractionEvent.SignIn),
    verificationType: z.literal(VerificationType.Password),
    identifier: interactionIdentifierGuard,
    user: z.object({ id: z.string() }).nullable(),
});
export const loggablePostSignInEventGuard = z.object({
    key: z.literal(LogtoActionKey.PostSignIn),
    interactionEvent: z.literal(InteractionEvent.SignIn),
    user: z.object({ id: z.string() }),
});
